'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import Image from 'next/image'
import styles from './TextMedia.module.scss'
import { ComponentProps } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'
import { CustomPlayButton } from '@/components/CustomPlayButton'

interface TextMediaProps extends ComponentProps {
  title?: string
  subtitle?: string
  description?: string
  mediaType?: 'image' | 'video'
  imageUrl?: string
  vimeoId?: string
  videoStyle?: 'embedded' | 'background'
  aspectRatio?: string
  // Legacy support
  mediaUrl?: string
  desktopLayout?: 'left' | 'right'
  mobileLayout?: 'top' | 'bottom'
  priority?: boolean
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

export const TextMedia: React.FC<TextMediaProps> = ({
  title = 'Section Title',
  subtitle = '',
  description = 'Section description goes here',
  mediaType = 'image',
  imageUrl,
  vimeoId,
  videoStyle = 'embedded',
  aspectRatio = '16/9',
  // Legacy support
  mediaUrl = '',
  desktopLayout = 'left',
  mobileLayout = 'top',
  priority = false,
  noTopPadding = false,
  noBottomPadding = false
}) => {
  // Animation states
  const [mediaInView, setMediaInView] = useState(false)
  const [titleInView, setTitleInView] = useState(false)
  const [subtitleInView, setSubtitleInView] = useState(false)
  const [descriptionInView, setDescriptionInView] = useState(false)
  
  // Refs for intersection observers
  const mediaRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const subtitleRef = useRef<HTMLParagraphElement>(null)
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  

  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  // Media intersection observer
  useEffect(() => {
    const element = mediaRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setMediaInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setMediaInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setTitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Subtitle intersection observer
  useEffect(() => {
    const element = subtitleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setSubtitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setSubtitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Description intersection observer
  useEffect(() => {
    const element = descriptionRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setDescriptionInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setDescriptionInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  const isVideo = (url: string) => {
    return url.match(/\.(mp4|webm|ogg|mov)$/i)
  }

  const isVimeoId = (value: string) => {
    // Check if it's just numbers (Vimeo ID) or contains vimeo.com
    return /^\d+$/.test(value.trim()) || value.includes('vimeo.com')
  }

  const getVimeoId = (value: string) => {
    if (/^\d+$/.test(value.trim())) {
      return value.trim()
    }
    // Extract ID from Vimeo URL
    const match = value.match(/vimeo\.com\/(\d+)/)
    return match ? match[1] : null
  }

  // Determine the actual media URL and type
  let actualMediaUrl: string | null = null
  let actualMediaType: 'image' | 'video' = 'image'
  
  // Use new props if available
  if (mediaType === 'video' && vimeoId) {
    actualMediaUrl = vimeoId.trim()
    actualMediaType = 'video'
  } else if (mediaType === 'image' && imageUrl) {
    actualMediaUrl = imageUrl
    actualMediaType = 'image'
  }
  // Fall back to legacy mediaUrl prop
  else if (mediaUrl) {
    actualMediaUrl = mediaUrl
    actualMediaType = isVimeoId(mediaUrl) ? 'video' : 'image'
  }
  
  const hasMedia = actualMediaUrl !== null
  const isVideoContent = (mediaType === 'video' && vimeoId) || (mediaUrl && isVimeoId(mediaUrl))
  const isBackgroundVideo = isVideoContent && ((mediaType === 'video' && videoStyle === 'background') || (mediaUrl && videoStyle === 'background'))

  return (
    <section className={styles['text-media']}>
      <div className={`${styles['text-media__container']} ${desktopLayout === 'right' ? styles['text-media__container--reverse'] : ''} ${styles[`text-media__container--mobile-${mobileLayout}`]} ${
        noTopPadding && noBottomPadding ? styles['text-media__container--no-vertical'] :
        noTopPadding ? styles['text-media__container--no-top'] :
        noBottomPadding ? styles['text-media__container--no-bottom'] : ''
      }`}>
        {hasMedia && (
          <motion.div 
            ref={mediaRef}
            className={`${styles['text-media__media-wrapper']} ${isVideoContent ? styles['text-media__media-wrapper--video'] : ''} ${isBackgroundVideo ? styles['text-media__media-wrapper--background'] : ''}`}
            style={isVideoContent ? { aspectRatio: aspectRatio } : {}}
            initial={{ opacity: isAdminMode ? 1 : 0 }}
            animate={{ opacity: mediaInView ? 1 : 0 }}
            transition={{
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            {(actualMediaType === 'video' || isVimeoId(actualMediaUrl!)) ? (
              <div className={styles['text-media__vimeo-wrapper']}>
                <iframe 
                  src={`https://player.vimeo.com/video/${getVimeoId(actualMediaUrl!)}?${
                    (mediaType === 'video' && videoStyle === 'background') || (mediaUrl && videoStyle === 'background')
                      ? 'background=1&autoplay=1&loop=1&byline=0&title=0&portrait=0&muted=1&dnt=1'
                      : 'badge=0&autopause=0&player_id=0&app_id=58479&byline=0&title=0&portrait=0&color=ffffff&controls=0&muted=1&dnt=1'
                  }`}
                  className={styles['text-media__vimeo']}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={title || 'Vimeo video'}
                />
                {/* Only show custom play button for embedded videos, not background videos */}
                {!((mediaType === 'video' && videoStyle === 'background') || (mediaUrl && videoStyle === 'background')) && (
                  <CustomPlayButton 
                    vimeoId={getVimeoId(actualMediaUrl!)!}
                    className={styles['text-media__custom-play-button']}
                  />
                )}
              </div>
            ) : isVideo(actualMediaUrl!) ? (
              <video 
                src={actualMediaUrl!} 
                className={styles['text-media__video']}
                controls
                playsInline
              />
            ) : (
              <Image 
                src={actualMediaUrl!} 
                alt={title || 'Media'}
                className={styles['text-media__image']}
                width={1200}
                height={800}
                quality={95}
                priority={priority}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 600px"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </motion.div>
        )}
        
        <div className={styles['text-media__content']}>
          <div className={styles['text-media__text']}>
            {title && (
              <motion.h2 
                ref={titleRef}
                className={`${styles['text-media__title']} ${subtitle ? styles['text-media__title--has-subtitle'] : ''}`}
                initial={{ opacity: isAdminMode ? 1 : 0 }}
                animate={{ opacity: titleInView ? 1 : 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2, // Appears after media
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
              />
            )}
            {subtitle && (
              <motion.p 
                ref={subtitleRef}
                className={styles['text-media__subtitle']}
                initial={{ opacity: isAdminMode ? 1 : 0 }}
                animate={{ opacity: subtitleInView ? 1 : 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.3, // Appears after title
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(subtitle) }}
              />
            )}
            {description && (
              <motion.p 
                ref={descriptionRef}
                className={styles['text-media__description']}
                initial={{ opacity: isAdminMode ? 1 : 0 }}
                animate={{ opacity: descriptionInView ? 1 : 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.4, // Appears after subtitle
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(description) }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  )
} 